// src/pages/employeeCommission/components/EmployeeProductLevelModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Search, Package } from 'lucide-react';
import { getProducts } from '../../../services/api/productSearch';
import {
  getEmployeeProductConfigs,
  upsertCommissionConfig,
  deleteCommissionConfig,
} from '../../../services/api/employeeCommission';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './PageModals.module.css';

const EMPTY_FORM = {
  productId: '',
  productLabel: '',
  confirmCommission: '',
  createCommission: '',
  calculationBasis: 'order_count',
};

const EmployeeProductLevelModal = ({ onClose, employee }) => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const res = await getEmployeeProductConfigs(employee.userId || employee.id);
      setConfigs(res?.configs || []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSearch = async (val) => {
    setProductSearch(val);
    if (val.length < 2) { setProductOptions([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await getProducts({ search: val, limit: 30 });
        setProductOptions(res?.products || []);
      } catch { }
    }, 400);
  };

  const handleSaveNewRow = async () => {
    if (!form.productId) { showError(t('employeeCommission.selectProductRequired')); return; }
    setSaving(true);
    try {
      await upsertCommissionConfig({
        userId: employee.userId || employee.id,
        productId: parseInt(form.productId),
        confirmCommission: parseFloat(form.confirmCommission) || 0,
        createCommission: parseFloat(form.createCommission) || 0,
        calculationBasis: form.calculationBasis,
      });
      success(t('employeeCommission.configSaved'));
      setForm(EMPTY_FORM);
      setProductSearch('');
      setProductOptions([]);
      setShowAddForm(false);
      await loadConfigs();
    } catch (err) {
      showError(err.response?.data?.message || t('employeeCommission.configSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    const confirmed = await confirm(t('employeeCommission.confirmDelete'));
    if (!confirmed) return;
    try {
      await deleteCommissionConfig(configId);
      success(t('employeeCommission.configDeleted'));
      await loadConfigs();
    } catch {
      showError(t('employeeCommission.configDeleteFailed'));
    }
  };

  const basisLabel = (key) => {
    const map = { order_count: t('employeeCommission.orderCount'), order_quantity: t('employeeCommission.orderQuantity'), order_value: t('employeeCommission.orderValue') };
    return map[key] || key;
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.modalLarge}`}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalTitle}>
              {t('employeeCommission.employeeProductTitle').replace('{{name}}', employee.name)}
            </h2>
            <p className={styles.modalSubtitle}>{t('employeeCommission.employeeProductSubtitle')}</p>
          </div>
          <button className={styles.closeButton} onClick={onClose}><X size={16} /></button>
        </div>

        <div className={styles.modalBody}>
          {loading ? (
            <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center', padding: '2rem' }}>
              {t('employeeCommission.loading')}
            </p>
          ) : (
            <>
              {configs.length === 0 && !showAddForm ? (
                <div className={styles.emptyState}>
                  <Package size={40} />
                  <p>{t('employeeCommission.noEmployeeProductConfigs')}</p>
                </div>
              ) : (
                <table className={styles.innerTable}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Basis</th>
                      <th>Confirm</th>
                      <th>Create</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((c) => (
                      <tr key={c.id}>
                        <td>{c.product?.name || '—'}</td>
                        <td style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>
                          {c.product?.sku || '—'}
                        </td>
                        <td>{basisLabel(c.calculationBasis)}</td>
                        <td>
                          {c.calculationBasis === 'order_value'
                            ? `${parseFloat(c.confirmCommission).toFixed(2)}%`
                            : `LKR ${parseFloat(c.confirmCommission).toFixed(2)}`}
                        </td>
                        <td>
                          {c.calculationBasis === 'order_value'
                            ? `${parseFloat(c.createCommission).toFixed(2)}%`
                            : `LKR ${parseFloat(c.createCommission).toFixed(2)}`}
                        </td>
                        <td>
                          <button className={styles.btnDanger} onClick={() => handleDelete(c.id)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Add product override form */}
              {showAddForm && (
                <div className={styles.addRowForm} style={{ marginTop: '1rem' }}>
                  <div>
                    <div className={styles.searchWrapper}>
                      <Search size={14} className={styles.searchIcon} />
                      <input
                        className={styles.searchInput}
                        style={{ fontSize: '0.8125rem', padding: '0.5rem 0.75rem 0.5rem 2.25rem' }}
                        placeholder={t('employeeCommission.searchProducts')}
                        value={productSearch}
                        onChange={(e) => handleProductSearch(e.target.value)}
                      />
                    </div>
                    {productOptions.length > 0 && (
                      <select
                        value={form.productId}
                        onChange={(e) => {
                          const p = productOptions.find((p) => String(p.productId || p.id) === e.target.value);
                          setForm({ ...form, productId: e.target.value, productLabel: p?.displayName || p?.name || '' });
                        }}
                        style={{ width: '100%', marginTop: '0.25rem' }}
                      >
                        <option value="">-- Select --</option>
                        {productOptions.map((p) => (
                          <option key={p.productId || p.id} value={p.productId || p.id}>
                            {p.displayName || p.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {form.productLabel && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-success-dark)' }}>✓ {form.productLabel}</span>
                    )}
                  </div>
                  <select
                    value={form.calculationBasis}
                    onChange={(e) => setForm({ ...form, calculationBasis: e.target.value })}
                    style={{ padding: '0.5rem', border: '2px solid var(--color-gray-200)', borderRadius: 6, fontSize: '0.8125rem' }}
                  >
                    {['order_count', 'order_quantity', 'order_value'].map((v) => (
                      <option key={v} value={v}>{basisLabel(v)}</option>
                    ))}
                  </select>
                  <input
                    type="number" min="0" step="0.01" placeholder="Confirm"
                    value={form.confirmCommission}
                    onChange={(e) => setForm({ ...form, confirmCommission: e.target.value })}
                    style={{ padding: '0.5rem', border: '2px solid var(--color-gray-200)', borderRadius: 6, fontSize: '0.8125rem', width: '100%' }}
                  />
                  <input
                    type="number" min="0" step="0.01" placeholder="Create"
                    value={form.createCommission}
                    onChange={(e) => setForm({ ...form, createCommission: e.target.value })}
                    style={{ padding: '0.5rem', border: '2px solid var(--color-gray-200)', borderRadius: 6, fontSize: '0.8125rem', width: '100%' }}
                  />
                  <button className={styles.btnPrimary} style={{ padding: '0.5rem 0.875rem', fontSize: '0.8125rem' }} onClick={handleSaveNewRow} disabled={saving}>
                    {saving ? <div className={styles.spinner} /> : t('employeeCommission.add')}
                  </button>
                </div>
              )}

              <button
                className={styles.btnSecondary}
                style={{ marginTop: '1rem' }}
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? <X size={14} /> : <Plus size={14} />}
                {showAddForm ? 'Cancel' : t('employeeCommission.addProductOverride')}
              </button>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose}>
            {t('employeeCommission.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeProductLevelModal;